import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import qs from 'query-string';
import _ from 'lodash';

import {CompanyCards} from './CompanyCard';
import Button from '../../components/ButtonNew';
import DocumentHeader from '../../components/DocumentHeader';
import DropdownInput from '../../components/DropdownInput';
import SignupModal from '../../components/SignupModal';
import {useAnalytics, useAppContext} from '../../hooks';
import {getGuides, getTags} from '../../api/api';
import {Feature} from '../../../../lib/constants/tags';
import {roleToRoleCategoryOptions} from '../../util';

const featuredCompanies = ['Google', 'Facebook', 'Amazon', 'Microsoft'];

const isFeaturedGuide = (g) => g.company && featuredCompanies.includes(g.company.name);

const GuidesPage = () => {
  const router = useRouter();
  const {role, company, query} = router.query;
  const {tracker} = useAnalytics();
  const {currentUser} = useAppContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [tags, setTags] = useState(null);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGuides().then((result) => {
      setGuides(result);
      setLoading(false);
    });
    tracker.viewGuidesPage();
  }, []);

  useEffect(() => {
    const getAndSetRoles = async () => {
      const guideTags = await getTags({feature: Feature.Guides});
      setTags(guideTags);
    };

    getAndSetRoles();
  }, []);

  const roleCategoryOptions = roleToRoleCategoryOptions(tags?.role, tags?.['role-category']);
  const companyOptions = _.sortBy(tags?.company?.map((c) => ({label: c.name, value: c.id})), 'label');

  const roleSelected = role ? _.find(tags?.role, {id: role})?.name : '';

  const onCloseModal = () => setModalOpen(false);

  const onClick = (e, guide) => {
    if (!currentUser) {
      e.preventDefault();
      tracker.guidesViewSignupWall({guide: guide.name});
      setModalOpen(true);
    }
  };

  // Filter guides by active role, if any
  const filteredGuides = guides.filter((guide) => {
    let roleMatch = true;
    let companyMatch = true;
    let queryMatch = true;

    if (role) {
      roleMatch = guide.roleTag === role;
    }

    if (company) {
      companyMatch = guide.company?.slug === company;
    }

    if (query) {
      const queryClean = (query || '').toLowerCase().trim();
      const guideName = guide.name ? guide.name.toLowerCase() : '';
      queryMatch = guideName.includes(queryClean);
    }

    return roleMatch && companyMatch && queryMatch;
  });
  // Show featured company guides first
  const orderedAndFilteredGuides = _.orderBy(filteredGuides, isFeaturedGuide, ['desc']);

  let guidesPageBody = (
    <>
      <CompanyCards
        items={orderedAndFilteredGuides}
        loading={loading}
        onClick={onClick}
      />
      {!currentUser
        && (
        <SignupModal
          source="guides"
          isOpen={modalOpen}
          dismissible
          onClose={onCloseModal}
        />
        )}
    </>
  );

  if (!loading && orderedAndFilteredGuides.length === 0) {
    guidesPageBody = (
      <div className="GuidesPage__coming">
        <div className="GuidesPage__coming__content">
          <h4 className="GuidesPage__coming__title">Coming soon!</h4>
          <div className="GuidesPage__coming__subtitle">
            {`We're still working on ${roleSelected} guides, stay tuned to learn more.
            Want to see a guide for a particular company? Let us know!`}
          </div>
        </div>
        <div className="GuidesPage__coming__button__wrapper">
          <Button
            className="GuidesPage__coming__button"
            href={`mailto:course-feedback@tryexponent.com?subject=Interested in new ${roleSelected} guide&body=Company: %0D%0AAnything else:`}
          >
            I&apos;m interested
          </Button>
        </div>
      </div>
    );
  }

  const pageTitle = roleSelected
    ? `${roleSelected} Interview Guides`
    : 'Interview Guides';

  const handleRoleFilter = (r) => {
    router.push({
      pathname: '/guides',
      search: qs.stringify({role: r || undefined, company, query}),
    }, null, {shallow: true});
  };

  const handleCompanyFilter = (c) => {
    router.push({
      pathname: '/guides',
      search: qs.stringify({role, company: c || undefined, query}),
    }, null, {shallow: true});
  };

  const onSearch = (q) => {
    router.push({
      pathname: '/guides',
      search: qs.stringify({role, company, query: q || undefined}),
    }, null, {shallow: true});
  };

  const onSearchDebounced = _.debounce(onSearch, 750, {maxWait: 2000});

  return (
    <div className="pt-10 pb-20 px-4 bg-white">
      <DocumentHeader title={pageTitle} />
      <div className="flex flex-col items-start max-w-6xl mx-auto mb-10">
        <h1 className="text-2xl md:text-4xl font-bold mb-5">
          {pageTitle}
        </h1>
        <div className="flex flex-col xs:flex-row xs:justify-between w-full gap-1">
          <div className="flex flex-col xs:flex-row">
            <DropdownInput
              placeholder="Filter by role"
              options={roleCategoryOptions}
              value={role}
              onChange={handleRoleFilter}
              showFilter
              className="mb-2 xs:mb-0 xs:mr-2"
            />
            <DropdownInput
              placeholder="Filter by company"
              options={companyOptions}
              value={company}
              onChange={handleCompanyFilter}
              showFilter
              className="mb-2 xs:mb-0 xs:mr-2"
            />
            <input
              className="rounded-lg shadow-sm border border-gray-300 px-3 py-2 focus:outline-none focus:border-gray-500"
              placeholder="Search"
              onChange={(e) => onSearchDebounced(e.target.value)}
              defaultValue={query}
            />
          </div>
          <Button
            asLink
            href="https://airtable.com/appROifRg71Nsizjd/shrH6423pjEJEdqZg"
            className="hidden md:flex py-2 px-3 text-sm ml-2"
            onClick={() => tracker.questionListClickContribute({button: 'top'})}
          >
            Request Guide
          </Button>
        </div>
      </div>
      {guidesPageBody}
    </div>
  );
};

export default GuidesPage;
